import { ICameraImage } from '@api/models';
import useDialog from '@hooks/use-dialog';
import { Breadcrumb, Button, Checkbox, Label, Table } from '@hyperflake/react-ui-library';
import { DatePicker } from '@library/datepicker';
import useCameraData from 'modules/cameras/hooks/useCameraData';
// import { DatePicker } from '@library/datepicker';
import useCameraImagesListData from 'modules/cameras/hooks/useCameraImagesListData';
import useClientData from 'modules/clients/hooks/useClientData';
import useProjectData from 'modules/projects/hooks/useProjectData';
import CameraSneakPeekModal from 'modules/shared/components/CameraSneakPeekModal/CameraSneakPeekModal';
import ConfirmationDialog from 'modules/shared/components/ConfirmationDialog/ConfirmationDialog';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { formatDatetime } from 'modules/shared/helpers/shared.utils';
import { DeleteIcon } from 'modules/shared/icons';
import moment from 'moment';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const CameraImagesList = () => {
    const [date, setDate] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const { clientId, projectId, cameraId } = useParams();
    const navigate = useNavigate();
    const { project } = useProjectData(clientId, projectId);
    const { client } = useClientData(clientId);
    const sneakPeakDialog = useDialog<ICameraImage>();
    const confirmationDialog = useDialog<string>();

    const { camera } = useCameraData(clientId, projectId, cameraId);
    const cameraImageListData = useCameraImagesListData(clientId, projectId, cameraId, date);

    const handleCheckboxChange = (e: any, rowId: string) => {
        if (e.target.checked) {
            setSelectedIds((prev) => [...prev, rowId]);
        } else {
            setSelectedIds((prev) => prev.filter((id) => id !== rowId));
            setSelectAllChecked(false);
        }
    };

    const handleSelectAll = (e: any) => {
        if (e.target.checked) {
            const allIds = cameraImageListData?.images.map((image) => image._id);
            setSelectedIds(allIds);
            setSelectAllChecked(true);
        } else {
            setSelectedIds([]);
            setSelectAllChecked(false);
        }
    };

    const handleDelete = async (name: string) => {
        try {
            await cameraImageListData.handleDeleteImage(selectedIds);
            toast.success('Image Deleted successfully.');
            setSelectedIds([]);
            setSelectAllChecked(false);
            confirmationDialog.hide();
        } catch (err: any) {
            console.log(err);
            toast.error(err?.message || 'Some error occurred, please try again.');
        }
    };

    return (
        <>
            <div className=" p-3">
                <Breadcrumb
                    items={[
                        {
                            label: 'Clients',
                            onClick: () => {
                                navigate('/');
                            },
                        },
                        {
                            label: client?.name,
                            onClick: () => {
                                navigate(`/clients/${client?._id}/client-details`);
                            },
                        },
                        {
                            label: project?.name,
                            onClick: () => {
                                navigate(`/clients/${client?._id}/projects/${projectId}/cameras`);
                            },
                        },
                        {
                            label: camera?.name,
                            onClick: () => {},
                        },
                    ]}
                />
            </div>
            <PageTemplate>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <PageHeading>Images</PageHeading>

                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
                        <div className="w-full sm:w-auto">
                            <Label className="block text-sm">Select Date</Label>
                            <DatePicker
                                className="flex h-10 w-full items-center rounded-md border border-grayscale-300 bg-transparent px-4 text-sm font-medium placeholder:text-grayscale-400 focus:outline focus:outline-2 focus:outline-primary/10 focus:border-primary"
                                onChange={(val) => setDate(moment(val).format('YYYYMMDD'))}
                            />
                        </div>

                        {selectedIds.length > 0 && (
                            <div className="w-full sm:w-auto">
                                <Button
                                    className="w-full sm:w-auto"
                                    onClick={() => {
                                        confirmationDialog.show(camera?.name);
                                    }}
                                >
                                    <DeleteIcon />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <div className="mt-6 overflow-x-auto">
                        <div className="min-w-full inline-block align-middle">
                            <Table
                                borderStyle="divider"
                                isLoading={cameraImageListData.isLoading}
                                data={cameraImageListData?.images}
                                className="min-w-[600px] sm:min-w-0 "
                                columns={[
                                    {
                                        header: (
                                            <Checkbox
                                                color="primary"
                                                checked={selectAllChecked}
                                                onChange={handleSelectAll}
                                            />
                                        ),
                                    },
                                    { header: 'ID' },
                                    { header: 'Date Time' },
                                    { header: 'Name' },
                                ]}
                                renderRow={(row) => (
                                    <Table.Row key={row._id}>
                                        <Table.Cell>
                                            <Checkbox
                                                color="primary"
                                                checked={selectedIds.includes(row._id)}
                                                onChange={(e) => handleCheckboxChange(e, row._id)}
                                            />
                                        </Table.Cell>
                                        <Table.Cell className="text-xs sm:text-sm break-all">{row._id}</Table.Cell>
                                        <Table.Cell className="whitespace-nowrap text-xs sm:text-sm">
                                            {row?.datetime
                                                ? formatDatetime(row?.datetime, 'h:mm a [·] DD MMM, YYYY')
                                                : '-'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div
                                                className="flex items-center gap-2 max-w-[180px] sm:max-w-none cursor-pointer"
                                                onClick={() => sneakPeakDialog.show(row)}
                                            >
                                                <img
                                                    src={row.urlThumb}
                                                    alt=""
                                                    width={48}
                                                    height={48}
                                                    className="shrink-0 rounded"
                                                />
                                                <span className="block min-w-0 truncate text-xs sm:text-sm">
                                                    {row?.name}
                                                </span>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            />
                        </div>
                    </div>
                </div>

                <CameraSneakPeekModal
                    show={sneakPeakDialog.isOpen}
                    onClose={sneakPeakDialog.hide}
                    cameraImage={sneakPeakDialog.data}
                />
                <ConfirmationDialog
                    show={confirmationDialog.isOpen}
                    onClose={confirmationDialog.hide}
                    handleDelete={handleDelete}
                    isLoading={cameraImageListData.isDeleting}
                    name={confirmationDialog.data}
                />
            </PageTemplate>
        </>
    );
};

export default CameraImagesList;
